import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectDestructuringEmpty from "@babel/runtime/helpers/objectDestructuringEmpty";
import _extends from "@babel/runtime/helpers/extends";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import { ConfluenceSearchClient } from './confluence-search-client';
import memo from 'memoize-one';
import deepEqual from 'deep-equal';
import { ConfluenceRecentsClient } from './confluence-recents-client';
import { CollaborationGraphClient, extractSiteUrl } from '../../common/clients';
import { v4 as uuid } from 'uuid';
import { ExperimentContext, useExperimentContext } from '@atlassian/search-experiment';
var defaultCollaborationGraphConfig = {
  collaborationGraphUrl: 'gateway/api/collaboration',
  useCollaborationGraphForRecents: false
};
var getSearchClientWithConfig = memo(function (config, collabGraphClient) {
  return new ConfluenceSearchClient(config, collabGraphClient);
}, deepEqual);
var getRecentsClientWithConfig = memo(function (config, collabGraphClient) {
  return new ConfluenceRecentsClient(config, collabGraphClient);
}, deepEqual);
var getCollabGraphClientWithConfig = memo(function (config) {
  return new CollaborationGraphClient({
    collaborationGraphUrl: config.collaborationGraphUrl,
    cloudId: config.cloudId,
    isMultiSite: config.isMultiSite,
    collabGraphSessionId: config.collabGraphSessionId,
    sites: config.sites
  });
}, deepEqual);
export var ConfluenceSearchClientsContext = /*#__PURE__*/React.createContext({});
var MISSING_PROVIDER_ERROR = new Error("Could not find all required Clients, make sure ConfluenceClientsProvider is created further up the tree");

//Will generate a session id to be sent to the CG endpoint as well as the frontend analytics
//This is created here as CG results are cached inside search dialog and is not invoked for subsequent use actions
var COLLABORATION_GRAPH_SESSION_ID = uuid();
export var withClients = function withClients(Component) {
  return function (props) {
    return /*#__PURE__*/React.createElement(ConfluenceSearchClientsContext.Consumer, null, function (_ref) {
      var searchClient = _ref.searchClient,
        recentClient = _ref.recentClient,
        cloudId = _ref.cloudId,
        _ref$siteUrl = _ref.siteUrl,
        siteUrl = _ref$siteUrl === void 0 ? '' : _ref$siteUrl,
        sites = _ref.sites,
        collabGraphSessionId = _ref.collabGraphSessionId;
      if (!searchClient || !recentClient) {
        throw MISSING_PROVIDER_ERROR;
      }
      var rest = _extends({}, (_objectDestructuringEmpty(props), props));
      return /*#__PURE__*/React.createElement(Component, _extends({}, rest, {
        searchClient: searchClient,
        recentClient: recentClient,
        cloudId: cloudId,
        collabGraphSessionId: collabGraphSessionId,
        siteUrl: siteUrl,
        sites: sites
      }));
    });
  };
};
export var ConfluenceClientsProvider = function ConfluenceClientsProvider(_ref2) {
  var children = _ref2.children,
    config = _ref2.config;
  var _defaultCollaboration = _objectSpread(_objectSpread({}, defaultCollaborationGraphConfig), config),
    baseUrl = _defaultCollaboration.baseUrl,
    cloudId = _defaultCollaboration.cloudId,
    collaborationGraphUrl = _defaultCollaboration.collaborationGraphUrl,
    aggregatorUrl = _defaultCollaboration.aggregatorUrl,
    isUserAnonymous = _defaultCollaboration.isUserAnonymous,
    useCollaborationGraphForRecents = _defaultCollaboration.useCollaborationGraphForRecents,
    recentItemsSupplier = _defaultCollaboration.recentItemsSupplier,
    _defaultCollaboration2 = _defaultCollaboration.sites,
    sites = _defaultCollaboration2 === void 0 ? [] : _defaultCollaboration2,
    collabGraphSessionId = _defaultCollaboration.collabGraphSessionId,
    scopes = _defaultCollaboration.scopes,
    useGraphQLClient = _defaultCollaboration.useGraphQLClient,
    useRecentWork = _defaultCollaboration.useRecentWork,
    recentWorkAPIUrl = _defaultCollaboration.recentWorkAPIUrl;
  var siteUrl = extractSiteUrl(config);
  var collabGraphClient = getCollabGraphClientWithConfig({
    collaborationGraphUrl: collaborationGraphUrl,
    cloudId: cloudId,
    isMultiSite: sites.length > 1,
    sites: sites,
    collabGraphSessionId: collabGraphSessionId
  });
  var experimentConfig = useExperimentContext();
  return /*#__PURE__*/React.createElement(ExperimentContext, null, /*#__PURE__*/React.createElement(ConfluenceSearchClientsContext.Provider, {
    value: {
      searchClient: getSearchClientWithConfig({
        isUserAnonymous: isUserAnonymous,
        url: aggregatorUrl,
        cloudId: cloudId,
        isCollaborationGraphEnabled: useCollaborationGraphForRecents,
        siteMasterList: sites,
        scopes: scopes,
        useGraphQLClient: useGraphQLClient,
        experimentConfig: experimentConfig
      }, collabGraphClient),
      recentClient: getRecentsClientWithConfig({
        url: baseUrl,
        isCollaborationGraphEnabled: useCollaborationGraphForRecents,
        recentItemsSupplier: recentItemsSupplier,
        useRecentWork: useRecentWork,
        recentWorkAPIUrl: recentWorkAPIUrl
      }, collabGraphClient),
      collabGraphClient: collabGraphClient,
      siteUrl: siteUrl,
      cloudId: cloudId,
      sites: sites,
      collabGraphSessionId: COLLABORATION_GRAPH_SESSION_ID
    }
  }, children));
};
export var useClients = function useClients() {
  var context = React.useContext(ConfluenceSearchClientsContext);
  if (!context.searchClient || !context.recentClient) {
    throw MISSING_PROVIDER_ERROR;
  }
  return context;
};
export var useRecentClient = function useRecentClient() {
  var context = React.useContext(ConfluenceSearchClientsContext);
  return context.recentClient;
};